import type { AttrType, MicroAppElementType, AppInterface } from '@micro-app/types'
import { formatLogMessage, defer, formatURL } from './libs/utils'
import { ObservedAttrName, appStatus, lifeCycles } from './constants'
import CreateApp, { appInstanceMap } from './create_app'
import {
  patchElementPrototypeMethods,
  releasePatches,
  rejectMicroAppStyle,
} from './source/patch'
import microApp from './micro_app'
import dispatchLifecyclesEvent from './interact/lifecycles_event'

// micro-app elelment 微应用的容器
export default class MicroAppElement extends HTMLElement implements MicroAppElementType {
  static microAppCount = 0 // 微应用数量
  static get observedAttributes (): string[] {
    return ['name', 'url']
  }

  name = ''
  url = ''
  version = '__VERSION__'
  isWating = false // 防止重复执行属性变化处理函数
  cacheData: Record<PropertyKey, unknown> | null = null // 当name不存在时设置data的缓存数据

  // 👇可配置项 6 个
  // shadowDom 是否开启shadowDOM
  // destory 卸载时是否强制删除缓存资源
  // inline js以内联script方式运行
  // disableScopecss 是否禁用css隔离，默认为false
  // disableSandbox 停用js沙盒
  // macro 用于解决vue3的异步渲染问题，和预加载的入参保持一致
  // baseUrl 路由前缀

  constructor () {
    super()
    if (++MicroAppElement.microAppCount === 1) {
      patchElementPrototypeMethods()
      rejectMicroAppStyle()
    }
  }

  // 元素插入文档中
  connectedCallback (): void {
    // 无论是否满足渲染条件，只要元素被创建就发送事件
    // 为了兼容react，created事件以异步发送
    defer(() => dispatchLifecyclesEvent(
      this,
      this.name,
      lifeCycles.CREATED,
    ))

    if (!this.name || !this.url) return

    if (this.getDisposeResult('shadowDOM')) {
      this.attachShadow({ mode: 'open' })
    }

    // 已经渲染过
    const app = appInstanceMap.get(this.name)
    if (app) {
      // 预加载，或卸载后重新渲染，则直接mount
      if (app.url === this.url && (app.isPrefetch || app.getAppStatus() === appStatus.UNMOUNT)) {
        app.isPrefetch = false
        // 保持渲染的异步执行，已避免多次渲染流程不统一导致的问题，如在react中的数据通信
        return defer(() => app.mount(
          this.shadowRoot ?? this,
          this.getDisposeResult('inline'),
          this.getAttribute('baseurl') ?? '',
        ))
      } else if (app.isPrefetch) {
        return console.error(
          formatLogMessage(`the url: ${this.url} is different from prefetch url: ${app.url}`)
        )
      } else {
        return console.error(
          formatLogMessage(`an app named ${this.name} already exists`)
        )
      }
    }

    // 创建应用
    this.handleCreate()
  }

  // 元素已经被删除
  disconnectedCallback (): void {
    this.handleUnmount(this.getDisposeResult('destory'))
    if (--MicroAppElement.microAppCount === 0) {
      releasePatches()
    }
  }

  // 属性发生变化
  attributeChangedCallback (attr: ObservedAttrName, _oldVal: string, newVal: string): void {
    if (this.legalAttribute(attr, newVal) && this[attr] !== newVal) {
      // 如果还没初始化，则直接赋值
      if (attr === ObservedAttrName.URL && !this.url) {
        newVal = formatURL(newVal)
        if (!newVal) {
          return console.error(
            formatLogMessage('Invalid attribute url')
          )
        }
        this.url = newVal
      } else if (attr === ObservedAttrName.NAME && !this.name) {
        if (this.cacheData) {
          // 如果初始时cacheData已经存在，则设置一次数据
          microApp.setData(newVal, this.cacheData)
          this.cacheData = null
        }
        this.name = newVal
      } else if (!this.isWating) {
        this.isWating = true
        defer(this.handleAttributeUpdate)
      }
    }
  }

  /**
   * 处理初始化后name或url发生变化
   * 只要name或url发生变化，则将旧应用完全卸载，并渲染新的应用
   * 注意：
   *  1、不推荐修改name和url，当前功能只作为兜底处理
   *  2、name属性修改后，绑定的样式会失效
   */
  handleAttributeUpdate = (): void => {
    this.isWating = false
    const attrName = this.getAttribute('name')
    const attrUrl = formatURL(this.getAttribute('url'))
    if (this.legalAttribute('name', attrName) && this.legalAttribute('url', attrUrl)) {
      if (attrName !== this.name && appInstanceMap.has(attrName!)) {
        const existApp = appInstanceMap.get(attrName!)!
        // 如果微应用还未卸载，则无法渲染
        if (existApp.getAppStatus() !== appStatus.UNMOUNT) {
          // 防止修改name失败导致样式丢失
          this.setAttribute('name', this.name)
          return console.error(
            formatLogMessage(`an app named ${this.name} already exists`)
          )
        }
      }

      if (attrName !== this.name || attrUrl !== this.url) {
        this.handleUnmount(true)
        this.name = attrName as string
        this.url = attrUrl
        // TODO: shadowdom
        this.innerHTML = ''
        this.handleCreate()
      }
    } else if (attrName !== this.name) {
      // 防止修改失败导致致样式丢失
      this.setAttribute('name', this.name)
    }
  }

  /**
   * 判断元素属性是否符合条件
   * @param name 属性名称
   * @param val 属性值
   */
  legalAttribute (name: string, val: AttrType): boolean {
    if (typeof val !== 'string' || !val) {
      console.error(
        formatLogMessage(`unexpected attribute ${name}, please check again`)
      )

      return false
    }

    return true
  }

  // 创建应用
  handleCreate (): void {
    const instance: AppInterface = new CreateApp({
      name: this.name!,
      url: this.url!,
      container: this.shadowRoot ?? this,
      inline: this.getDisposeResult('inline'),
      scopecss: !(this.getDisposeResult('disableScopecss') || this.getDisposeResult('shadowDOM')),
      useSandbox: !this.getDisposeResult('disableSandbox'),
      macro: this.getDisposeResult('macro'),
      baseurl: this.getAttribute('baseurl') ?? '',
    })

    // 存储
    appInstanceMap.set(this.name!, instance)
  }

  /**
   * 卸载应用
   * @param destory 是否完全销毁
   */
  handleUnmount (destory: boolean): void {
    const app = appInstanceMap.get(this.name!)
    if (app) app.unmount(destory)
  }

  /**
   * 获取配置结果
   * 全局的优先级最低
   * @param name 名称
   */
  getDisposeResult (name: string): boolean {
    // @ts-ignore
    return (this.hasAttribute(name) || microApp[name]) && this.getAttribute(name) !== 'false'
  }

  /**
   * 基座应用传入的数据
   */
  set data (value: Record<PropertyKey, unknown> | null) {
    // data和name属性的先后顺序不确定
    if (this.name) {
      microApp.setData(this.name, value!)
    } else {
      this.cacheData = value
    }
  }

  /**
   * data取值只在jsx-custom-event中使用一次
   */
  get data (): Record<PropertyKey, unknown> | null {
    if (this.name) {
      return microApp.getData(this.name, true)
    } else if (this.cacheData) {
      return this.cacheData
    }
    return null
  }
}
